// MARK://dhk8/admin-shell/components/ImageUploader
// Drag-and-drop / click image picker that uploads through the existing media
// client (`uploadImage` → { url, key }) and reports the resulting URL via
// onChange. Shows a live preview with a remove button and an "uploading" state.
// Also exports `ThumbnailCell` for compact image previews in list rows.
//
// Self-contained: depends only on React + lucide-react + lib/clients/media. All
// styling is scoped `nui-*` CSS (never touches styles.css/tokens.css).
import { useRef, useState } from 'react'
import { ImagePlus, Loader2, X } from 'lucide-react'
import { uploadImage } from '../lib/clients/media.js'

const IMAGE_UPLOADER_CSS = `
@keyframes nui-iu-spin { to { transform: rotate(360deg); } }
.nui-iu-spin { animation: nui-iu-spin 0.8s linear infinite; }

.nui-iu {
  display: flex; flex-direction: column; gap: 6px;
}
.nui-iu__drop {
  position: relative;
  display: flex; align-items: center; justify-content: center; gap: var(--pos-space-2, 8px);
  min-height: 132px; padding: var(--pos-space-4, 16px);
  border: 1px dashed var(--pos-separator-strong);
  border-radius: var(--pos-radius-lg, 12px);
  background: var(--pos-canvas);
  color: var(--pos-fg-tertiary);
  font-family: var(--font-sans);
  font-size: var(--pos-text-sm, 13px);
  cursor: pointer; text-align: center;
  transition: border-color var(--pos-duration-fast, 120ms) var(--pos-ease-out),
              background var(--pos-duration-fast, 120ms) var(--pos-ease-out);
}
.nui-iu__drop:hover { border-color: var(--pos-tint); background: var(--pos-elevated); }
.nui-iu__drop.is-drag { border-color: var(--pos-tint); background: var(--pos-tint-soft); color: var(--pos-fg); }
.nui-iu__drop.is-busy { cursor: progress; }
.nui-iu__hint { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.nui-iu__hint small { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); }

.nui-iu__preview {
  position: relative; display: inline-block;
  border-radius: var(--pos-radius-lg, 12px); overflow: hidden;
  border: 1px solid var(--pos-separator-strong);
}
.nui-iu__preview img { display: block; width: 100%; max-width: 220px; height: auto; object-fit: cover; }
.nui-iu__remove {
  position: absolute; top: 6px; right: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; padding: 0;
  border: none; border-radius: var(--pos-radius-pill, 9999px);
  background: var(--pos-overlay); color: #fff; cursor: pointer;
}
.nui-iu__remove:hover { background: var(--pos-danger); }
.nui-iu__err { font-size: var(--pos-text-xs, 12px); color: var(--pos-danger); }

.nui-thumb {
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
  border-radius: var(--pos-radius-md, 8px);
  background: var(--pos-elevated);
  border: 1px solid var(--pos-separator);
  color: var(--pos-fg-tertiary);
}
.nui-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

export interface ImageUploaderProps {
  value: string | null
  onChange: (url: string | null) => void
}

export default function ImageUploader({ value, onChange }: ImageUploaderProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function upload(file: File | undefined | null) {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setErr('El archivo debe ser una imagen.')
      return
    }
    setErr(null)
    setUploading(true)
    try {
      const media = await uploadImage(file)
      onChange(media.url)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo subir la imagen.')
    } finally {
      setUploading(false)
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    if (uploading) return
    void upload(e.dataTransfer.files?.[0])
  }

  return (
    <div className="nui-iu">
      <style>{IMAGE_UPLOADER_CSS}</style>

      {value ? (
        <div className="nui-iu__preview">
          <img src={value} alt="" />
          <button
            type="button"
            className="nui-iu__remove"
            aria-label="Quitar imagen"
            onClick={() => { setErr(null); onChange(null) }}
          >
            <X size={16} aria-hidden />
          </button>
        </div>
      ) : (
        <div
          className={`nui-iu__drop${dragging ? ' is-drag' : ''}${uploading ? ' is-busy' : ''}`}
          role="button"
          tabIndex={0}
          aria-label="Subir imagen"
          aria-busy={uploading}
          onClick={() => { if (!uploading) fileRef.current?.click() }}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !uploading) { e.preventDefault(); fileRef.current?.click() } }}
          onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          {uploading ? (
            <span className="nui-iu__hint">
              <Loader2 size={22} className="nui-iu-spin" aria-hidden />
              <span>Subiendo…</span>
            </span>
          ) : (
            <span className="nui-iu__hint">
              <ImagePlus size={22} aria-hidden />
              <span>Arrastra una imagen o haz clic para subir</span>
              <small>PNG, JPG o WEBP</small>
            </span>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = '' }}
      />

      {err ? <span className="nui-iu__err">{err}</span> : null}
    </div>
  )
}

export interface ThumbnailCellProps {
  url: string | null | undefined
  /** Square edge length in px (default 40). */
  size?: number
}

/** Compact square image preview for list rows; shows a placeholder glyph when empty. */
export function ThumbnailCell({ url, size = 40 }: ThumbnailCellProps) {
  return (
    <span className="nui-thumb" style={{ width: size, height: size }}>
      <style>{IMAGE_UPLOADER_CSS}</style>
      {url ? <img src={url} alt="" /> : <ImagePlus size={Math.round(size * 0.45)} aria-hidden />}
    </span>
  )
}
